import { alreadyHasPhotos, claimRequest, settleRequest, unsureSaveWords } from '../client-ref.js';
import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { getDisplayUnits, formatVolume } from '../units.js';
import { paramLabelLower } from '../../../shared/param-labels.js';
import { DOSE_TO_TANK_PARAM } from '../params.js';
import { useConfirm } from './ConfirmDialog.jsx';
import { STATUS_LABEL, keptForLabel, residentName } from '../livestock-journey.js';
import { ResidentEditSheet } from './LivestockResidents.jsx';
import { JourneySheet } from './LivestockHistory.jsx';
import { NotePhotoPicker } from './NotePhotoPicker.jsx';
import { uploadNotePhotos, photosFailedMessage } from '../note-photos.js';
import { locateJournalResident } from '../journal-resident.js';
import { doseLogUnitLabel } from '../../../shared/dose-log.js';

// The tank journal: one chronological feed of everything the keeper
// did — water changes, doses, livestock and equipment additions,
// photos, ICP imports — plus free-text notes typed right here. The
// question it answers: "what did I change before things went
// sideways?" Everything except notes is derived server-side from the
// entity tables, so logging a water change or a dose lands here with
// no extra step.

const PAGE = 20;
const FIRST_PAGE = 8;

const TYPE_BADGE = {
  note: 'NOTE',
  water_change: 'WATER',
  dose: 'DOSE',
  livestock: 'STOCK',
  livestock_end: 'STOCK',
  livestock_note: 'STOCK',
  equipment: 'GEAR',
  photo: 'PHOTO',
  icp: 'ICP',
};

function entryLine(e, units) {
  switch (e.type) {
    case 'water_change': {
      const vol = formatVolume(e.gallons, units.volume);
      const pct = Number.isFinite(e.percent) ? ` (${Math.round(e.percent)}%)` : '';
      const salt = e.salt_brand ? ` · ${e.salt_brand}` : '';
      return `Water change · ${vol}${pct}${salt}`;
    }
    case 'dose': {
      const label =
        e.param && e.param !== 'other'
          ? ` ${paramLabelLower(DOSE_TO_TANK_PARAM[e.param] || e.param)}`
          : '';
      const amount =
        e.unit && Number.isFinite(e.amount)
          ? `${e.amount} ${doseLogUnitLabel(e.unit)}`
          : Number.isFinite(e.amount_ml)
            ? `${e.amount_ml} mL`
            : '';
      const product = e.product_label ? ` ${e.product_label}` : '';
      return `Dosed${label} · ${amount}${product}`;
    }
    case 'livestock': {
      const qty = e.qty > 1 ? ` ×${e.qty}` : '';
      return `Added ${residentName(e)}${qty}`;
    }
    case 'livestock_note':
      return `${residentName(e)}: ${e.body || 'photo'}`;
    case 'note':
      return e.body || 'Photo';
    case 'livestock_end': {
      const qty = e.qty > 1 ? ` ×${e.qty}` : '';
      const kept = keptForLabel(e.added_at, e.ts);
      const what = (STATUS_LABEL[e.status] || e.status || '').toLowerCase();
      const note = e.note ? ` · ${e.note}` : '';
      return `${residentName(e)}${qty} ${what}${kept ? ` · kept ${kept}` : ''}${note}`;
    }
    case 'equipment': {
      const what = [e.brand, e.model].filter(Boolean).join(' ');
      return `Added ${e.kind}${what ? ` · ${what}` : ''}`;
    }
    case 'photo':
      return 'Tank photo';
    case 'icp':
      return `ICP report imported · ${e.count} element${e.count === 1 ? '' : 's'}`;
    default:
      return e.body || '';
  }
}

function entryDate(ts) {
  const d = new Date(ts);
  const now = new Date();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}

// Entries about one resident open that resident: its card while it is
// in the tank, its record once it has left.
const RESIDENT_TYPES = new Set(['livestock', 'livestock_end', 'livestock_note']);

// asPage: the Journal page (Tank tab, and Log's "Note or photo" with
// compose), always open; Home's section keeps the keeper's own open /
// closed choice.
export function JournalSection({ tank, onReload, asPage = false, compose = false }) {
  const confirm = useConfirm();
  const canWrite = tank.role !== 'viewer';
  const units = getDisplayUnits(tank);
  const [entries, setEntries] = useState(null); // null = loading
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [draft, setDraft] = useState('');
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(() => {
    if (asPage) return true;
    try {
      return localStorage.getItem('aqs.journalOpen') === '1';
    } catch {
      return false;
    }
  });
  const inputRef = useRef(null);
  const [resident, setResident] = useState(null);
  const setOpenSticky = (v) => {
    setOpen(v);
    if (asPage) return;
    try {
      localStorage.setItem('aqs.journalOpen', v ? '1' : '0');
    } catch {
      /* private mode — non-fatal */
    }
  };

  const loaded = entries !== null;
  useEffect(() => {
    if (compose && loaded) inputRef.current?.focus();
  }, [compose, loaded]);

  const loadFirst = async () => {
    try {
      const feed = await api(`/api/tanks/${tank.id}/journal?limit=${FIRST_PAGE}`);
      setEntries(feed.entries || []);
      setNextBefore(feed.next_before ? { ts: feed.next_before, id: feed.next_before_id } : null);
    } catch (e) {
      setErr(e.message || 'Could not load the journal.');
      setEntries([]);
    }
  };

  useEffect(() => {
    setEntries(null);
    setNextBefore(null);
    setErr('');
    loadFirst();
  }, [tank.id]);

  const loadMore = async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    setErr('');
    try {
      const feed = await api(
        `/api/tanks/${tank.id}/journal?limit=${PAGE}&before=${encodeURIComponent(nextBefore.ts)}` +
          (nextBefore.id ? `&before_id=${encodeURIComponent(nextBefore.id)}` : '')
      );
      setEntries((prev) => {
        const seen = new Set((prev || []).map((e) => e.id));
        return [...(prev || []), ...(feed.entries || []).filter((e) => !seen.has(e.id))];
      });
      setNextBefore(feed.next_before ? { ts: feed.next_before, id: feed.next_before_id } : null);
    } catch (e) {
      setErr(e.message || 'Could not load older entries.');
    } finally {
      setLoadingMore(false);
    }
  };

  const saveNote = async () => {
    const body = draft.trim();
    if ((!body && !photos.length) || saving) return;
    setSaving(true);
    setErr('');
    let posted = false;
    try {
      const scope = `journal:${tank.id}`;
      const note = await api(`/api/tanks/${tank.id}/journal/notes`, {
        method: 'POST',
        body: {
          body,
          with_photos: photos.length > 0,
          client_ref: claimRequest(scope, JSON.stringify([body, photos.length])),
        },
      });
      posted = true;
      settleRequest(scope);
      const photoErrors = [];
      // The same note again (its reply was lost): its photos went already.
      const failed = alreadyHasPhotos(note, photos.length)
        ? 0
        : await uploadNotePhotos(
            `/api/tanks/${tank.id}/journal/notes/${note.id}/images`,
            photos,
            photoErrors
          );
      // A photo-only note whose photos all failed would be an empty row.
      if (!body && failed === photos.length) {
        await api(`/api/tanks/${tank.id}/journal/notes/${note.id}`, { method: 'DELETE' }).catch(
          () => {}
        );
        setErr(photoErrors[0] || 'The photo could not be uploaded. Try again.');
      } else {
        setDraft('');
        setPhotos([]);
        setErr(photosFailedMessage(failed, photos.length, photoErrors[0]));
      }
      await loadFirst();
    } catch (e) {
      // Only an unanswered save may already be saved; once it answered, the
      // note is saved and its id settled.
      setErr((!posted && unsureSaveWords(e)) || e.message || 'Could not save the note.');
    } finally {
      setSaving(false);
    }
  };

  // A resident's note stays in the journal of the tank it was written in,
  // but its photos are served through the tank the resident lives in now
  // (or the account path once that tank is gone): after a move out of
  // quarantine the QT's path no longer finds it.
  const imageUrl = (e, id) => {
    if (e.type !== 'livestock_note')
      return `/api/tanks/${tank.id}/journal/notes/${e.note_id}/images/${id}`;
    const home = e.livestock_tank_id === undefined ? tank.id : e.livestock_tank_id;
    return home
      ? `/api/tanks/${home}/livestock/${e.livestock_id}/image/${id}`
      : `/api/livestock/${e.livestock_id}/image/${id}`;
  };

  const removeNote = async (e) => {
    const ok = await confirm({
      title: 'Delete this note?',
      body: e.image_ids?.length ? 'Its photos are deleted too.' : undefined,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api(`/api/tanks/${tank.id}/journal/notes/${e.note_id}`, { method: 'DELETE' });
      setEntries((prev) => (prev || []).filter((x) => x.id !== e.id));
    } catch (ex) {
      setErr(ex.message || 'Could not delete the note.');
    }
  };

  const openResident = async (e) => {
    setErr('');
    try {
      const found = await locateJournalResident(tank, e.livestock_id, api);
      if (!found) return setErr('That resident is no longer in the records.');
      setResident({ row: found.row, ended: found.view === 'record' });
    } catch (ex) {
      setErr(ex.message || 'Could not open it.');
    }
  };

  const afterResidentChange = async () => {
    if (onReload) await onReload();
    await loadFirst();
  };

  if (entries === null) return null; // first paint — no skeleton flash

  const latest = entries[0] || null;
  const headText = (
    <>
      <span className="hv-journal-eyebrow font-heading">Journal</span>
      <span className="hv-journal-meta font-mono">
        {latest ? `last ${entryDate(latest.ts)}` : 'nothing logged yet'}
      </span>
    </>
  );

  return (
    <section className="hv-journal" aria-label="Tank journal">
      <div className="hv-journal-head">
        {/* On its own page the journal never folds away: tapping the
          heading emptied the page. */}
        {asPage ? (
          <div className="hv-journal-toggle">{headText}</div>
        ) : (
          <button
            type="button"
            className="hv-journal-toggle"
            onClick={() => setOpenSticky(!open)}
            aria-expanded={open}
          >
            <span className={`hv-journal-chevron${open ? ' open' : ''}`} aria-hidden="true">
              ▸
            </span>
            {headText}
          </button>
        )}
        {canWrite && (
          <button
            type="button"
            className="hv-journal-add font-heading"
            onClick={() => {
              setOpenSticky(true);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
          >
            + Add note
          </button>
        )}
      </div>

      {open && (
        <>
          {canWrite && (
            <div className="hv-journal-composer">
              <input
                ref={inputRef}
                type="text"
                value={draft}
                maxLength={1000}
                placeholder="What you saw, what you did…"
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveNote();
                }}
              />
              <button
                type="button"
                className="font-heading"
                onClick={saveNote}
                disabled={saving || (!draft.trim() && !photos.length)}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          )}
          {canWrite && <NotePhotoPicker files={photos} onChange={setPhotos} disabled={saving} />}

          {entries.length === 0 ? (
            <p className="hv-journal-empty">
              Water changes, doses, new livestock, photos, and your own notes will show up here as
              one story of the tank.
            </p>
          ) : (
            <ul className="hv-journal-list">
              {entries.map((e) => (
                <li key={e.id} className="hv-journal-row">
                  <span className="hv-journal-date font-mono">{entryDate(e.ts)}</span>
                  <span className={`hv-journal-badge font-heading hv-journal-badge--${e.type}`}>
                    {TYPE_BADGE[e.type] || 'LOG'}
                  </span>
                  <span className="hv-journal-main">
                    {RESIDENT_TYPES.has(e.type) && e.livestock_id ? (
                      <button
                        type="button"
                        className="hv-journal-text hv-journal-link"
                        onClick={() => openResident(e)}
                      >
                        {entryLine(e, units)}
                      </button>
                    ) : e.type === 'photo' ? (
                      <a
                        className="hv-journal-text hv-journal-link"
                        href={`/api/tanks/${tank.id}/photos/${e.photo_id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <img
                          className="hv-journal-thumb"
                          src={`/api/tanks/${tank.id}/photos/${e.photo_id}`}
                          alt=""
                          loading="lazy"
                        />
                        {entryLine(e, units)}
                        {e.note ? <span className="hv-journal-note"> — {e.note}</span> : null}
                      </a>
                    ) : (
                      <span className="hv-journal-text">
                        {entryLine(e, units)}
                        {e.type !== 'note' && e.note ? (
                          <span className="hv-journal-note"> — {e.note}</span>
                        ) : null}
                      </span>
                    )}
                    {e.image_ids?.length > 0 && (
                      <span className="hv-journal-photos">
                        {e.image_ids.map((id) => (
                          <a key={id} href={imageUrl(e, id)} target="_blank" rel="noreferrer">
                            <img src={imageUrl(e, id)} alt="" loading="lazy" />
                          </a>
                        ))}
                      </span>
                    )}
                  </span>
                  {canWrite && e.type === 'note' && (
                    <button
                      type="button"
                      className="hv-journal-x"
                      onClick={() => removeNote(e)}
                      aria-label="Delete this note"
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {nextBefore && (
            <button
              type="button"
              className="hv-journal-more font-heading"
              onClick={loadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : 'Show older'}
            </button>
          )}
        </>
      )}
      {err && (
        <p className="hv-journal-err" role="alert">
          {err}
        </p>
      )}
      {resident && !resident.ended && (
        <ResidentEditSheet
          tank={tank}
          resident={resident.row}
          onClose={() => {
            setResident(null);
            loadFirst();
          }}
          onReload={afterResidentChange}
        />
      )}
      {resident && resident.ended && (
        <JourneySheet
          resident={resident.row}
          canWrite={tank.role !== 'viewer'}
          isOwner={tank.role === 'owner'}
          fallbackUnits={tank.display_units || null}
          onClose={() => {
            setResident(null);
            loadFirst();
          }}
          onChanged={afterResidentChange}
        />
      )}
    </section>
  );
}
